/**
 * @description Context provider that manages global auth state, and exposes authentication methods and user data to the component tree.
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { LoginRequest, RegisterRequest,RegistrationResult, User } from '../types/auth.types';

interface AuthContextType {
  register: (data: RegisterRequest) => Promise<RegistrationResult>;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  logout: () => void;
  updateProfilePicture: (profilePicture: string | null) => void;
  updateUsername: (username: string) => void;
  updateEmail: (email: string) => void;
  updateExternalLinkWarningDisabled: (disabled: boolean) => void;
  updateHomeAiSearchEnabled: (enabled: boolean | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkAuth = async () => {
      const restoredUser = await authService.restoreSession();
      if (restoredUser) {
        setIsAuthenticated(true);
        setUser(restoredUser);
      }
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (data: LoginRequest) => {

    const response = await authService.login(data);

    setIsAuthenticated(true);
    setUser(response.user);
  };

  const register = async (data: RegisterRequest) => {
    return await authService.register(data);
  };

  const logout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const updateProfilePicture = (profilePicture: string | null) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, profilePicture };
      try {
        localStorage.setItem("user", JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  };

  const updateUsername = (username: string) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, username };
      try {
        localStorage.setItem("user", JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  };

  const updateEmail = (email: string) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, email };
      try {
        localStorage.setItem("user", JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  };

  const updateExternalLinkWarningDisabled = (disabled: boolean) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, externalLinkWarningDisabled: disabled };
      try {
        localStorage.setItem("user", JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  };

  const updateHomeAiSearchEnabled = (enabled: boolean | null) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, homeAiSearchEnabled: enabled };
      try {
        localStorage.setItem("user", JSON.stringify(next));
      } catch {
        // ignore storage failures
      }
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      login,
      register,
      logout,
      updateProfilePicture,
      updateUsername,
      updateEmail,
      updateExternalLinkWarningDisabled,
      updateHomeAiSearchEnabled,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
